import React, { useState } from 'react';
import type { BookingFormProps } from './types';

const BookingForm: React.FC<BookingFormProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    notes: '',
  });

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [errors, setErrors] = useState({
    contact: '',
    name: '',
    gender: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    
    // Clear errors when user starts typing
    if (e.target.name === 'email' || e.target.name === 'phone') {
      setErrors(prev => ({ ...prev, contact: '' }));
    } else if (e.target.name === 'name') {
      setErrors(prev => ({ ...prev, name: '' }));
    } else if (e.target.name === 'gender') {
      setErrors(prev => ({ ...prev, gender: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = { contact: '', name: '', gender: '' };
    
    // Check if at least one contact method is provided
    if (!formData.email.trim() && !formData.phone.trim()) {
      newErrors.contact = 'Please provide either an email address or phone number';
    }
    
    // Check name
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }
    
    // Check gender
    if (!formData.gender) {
      newErrors.gender = 'Please select your gender';
    }
    
    setErrors(newErrors);
    return !newErrors.contact && !newErrors.name && !newErrors.gender;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    onSubmit({
      name: formData.name,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      gender: formData.gender,
      notes: formData.notes || undefined,
    });
  };

  const isFieldActive = (fieldName: string) => {
    return focusedField === fieldName || formData[fieldName as keyof typeof formData];
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Full Name */}
      <div className="relative">
        <div className="absolute left-4 top-4 pointer-events-none">
          <span className="material-symbols-outlined text-gray-500 dark:text-gray-400 text-xl">
            person
          </span>
        </div>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          onFocus={() => setFocusedField('name')}
          onBlur={() => setFocusedField(null)}
          required
          className="w-full pl-12 pr-4 pt-6 pb-2 border border-gray-300 dark:border-gray-600 bg-[#202022] focus:border-primary dark:focus:border-primary outline-none transition-colors duration-200 text-gray-900 dark:text-white rounded-lg"
        />
        <label
          className={`absolute left-12 transition-all duration-200 pointer-events-none ${
            isFieldActive('name')
              ? 'top-1 text-xs text-primary dark:text-primary'
              : 'top-4 text-base text-gray-500 dark:text-gray-400'
          }`}
        >
          Full Name *
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email */}
        <div className="relative">
          <div className="absolute left-4 top-4 pointer-events-none">
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400 text-xl">
              mail
            </span>
          </div>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onFocus={() => setFocusedField('email')}
            onBlur={() => setFocusedField(null)}
            className={`w-full pl-12 pr-4 pt-6 pb-2 border ${errors.contact ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-[#202022] focus:border-primary dark:focus:border-primary outline-none transition-colors duration-200 text-gray-900 dark:text-white rounded-lg`}
          />
          <label
            className={`absolute left-12 transition-all duration-200 pointer-events-none ${
              isFieldActive('email')
                ? 'top-1 text-xs text-primary dark:text-primary'
                : 'top-4 text-base text-gray-500 dark:text-gray-400'
            }`}
          >
            Email Address
          </label>
        </div>

        {/* Phone */}
        <div className="relative">
          <div className="absolute left-4 top-4 pointer-events-none">
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400 text-xl">
              phone
            </span>
          </div>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onFocus={() => setFocusedField('phone')}
            onBlur={() => setFocusedField(null)}
            className={`w-full pl-12 pr-4 pt-6 pb-2 border ${errors.contact ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-[#202022] focus:border-primary dark:focus:border-primary outline-none transition-colors duration-200 text-gray-900 dark:text-white rounded-lg`}
          />
          <label
            className={`absolute left-12 transition-all duration-200 pointer-events-none ${
              isFieldActive('phone')
                ? 'top-1 text-xs text-primary dark:text-primary'
                : 'top-4 text-base text-gray-500 dark:text-gray-400'
            }`}
          >
            Phone Number
          </label>
        </div>
      </div>

      {/* Contact Error Message */}
      {errors.contact && (
        <p className="text-red-500 text-sm -mt-4">{errors.contact}</p>
      )}

      {/* Gender */}
      <div className="relative">
        <div className="absolute left-4 top-4 pointer-events-none">
          <span className="material-symbols-outlined text-gray-500 dark:text-gray-400 text-xl">
            wc
          </span>
        </div>
        <select
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          onFocus={() => setFocusedField('gender')}
          onBlur={() => setFocusedField(null)}
          required
          className="w-full pl-12 pr-10 pt-6 pb-2 border border-gray-300 dark:border-gray-600 bg-[#202022] focus:border-primary dark:focus:border-primary outline-none transition-colors duration-200 text-gray-900 dark:text-white appearance-none cursor-pointer rounded-lg"
        >
          <option value="" disabled></option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>
        <label
          className={`absolute left-12 transition-all duration-200 pointer-events-none ${
            isFieldActive('gender')
              ? 'top-1 text-xs text-primary dark:text-primary'
              : 'top-4 text-base text-gray-500 dark:text-gray-400'
          }`}
        >
          Gender *
        </label>
        <div className="absolute right-4 top-4 pointer-events-none">
          <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Notes */}
      <div className="relative">
        <div className="absolute left-4 top-4 pointer-events-none">
          <span className="material-symbols-outlined text-gray-500 dark:text-gray-400 text-xl">
            notes
          </span>
        </div>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          onFocus={() => setFocusedField('notes')}
          onBlur={() => setFocusedField(null)}
          rows={4}
          className="w-full pl-12 pr-4 pt-6 pb-2 border border-gray-300 dark:border-gray-600 bg-[#202022] focus:border-primary dark:focus:border-primary outline-none transition-colors duration-200 text-gray-900 dark:text-white resize-none rounded-lg"
        />
        <label
          className={`absolute left-12 transition-all duration-200 pointer-events-none ${
            isFieldActive('notes')
              ? 'top-1 text-xs text-primary dark:text-primary'
              : 'top-4 text-base text-gray-500 dark:text-gray-400'
          }`}
        >
          Additional Notes (Optional)
        </label>
      </div>

      <div className="pt-4">
        <button
          type="submit"
          className="w-full py-4 px-6 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all font-semibold text-lg hover:scale-105"
        >
          Confirm Booking
        </button>
      </div>
    </form>
  );
};

export default BookingForm;
